import { useState, useMemo } from 'react';
import { useField, useForm } from 'react-jeff';
import _cloneDeep from 'lodash.clonedeep';
import _sumBy from 'lodash/sumBy';
import { AddressService } from '../services/address';
import { KonakartService } from '../services/konakart';
import { MyAccountService } from '../services/myaccount';
import { OrderService } from '../services/order';
import { PrequalService } from '../services/prequal';
import { JSONConfigService } from '../services/jsonConfig';
import { MyAccountSWR } from '../swr/myaccount';
import { useBroadband } from './useBroadband';
import { useVoiceStore } from '../store/voiceStore';
import { useToastContainer } from './useToastContainer';
import { useCouponRemovalToast } from './useCouponRemovalToast';
import { useUpgradableBoxesStore } from '../store/upgradableBoxesStore';
import {
  CouponTypeValue,
  CouponTypes,
  OrderDetails,
  OrderNumber,
  T_Coupon,
  T_Order,
  T_OrderProduct,
  T_Product,
  T_Profile,
  UserGroupType,
} from '../types';
import { NZPostAddressService, useCouponStore } from '../entry';
import { useCartContainer } from './useCartContainer';
import { useManagePackage } from './useManagePackage';
import { useNewUpgradeOrderStore } from '../store/useNewUpgradeOrderStore';
import { useAnalytics } from '../helpers/useAnalytics';
import { createRequestBody } from './orderDataBuilders/orderRequest';
import { TrackerService } from '../services/tracker';
import {
  dataCouponCodeNoContract,
  messages,
  productSkuIds,
  PACKAGE_TYPE,
  categoryIds,
  VTVOccurrenceTypes,
  VTVMigrationType,
  DartEligibilityCode,
  SPLITIO_KEY,
} from '../config';
import { SentryService } from '../services/sentry';
import { useFreeProductStore } from '../store/freeProductStore';
import { useCoupon } from './useApplyCoupon';
import { formatMoney } from './format';
import { buildCustomerInfo } from './orderDataBuilders/helper';
import { orderStoreApi, setOrderRequest } from '../store/orderStore';
import { useKeyThingsToKnowField, useTermsAndConditions } from '../helpers/termsAndConditionsFields';
import { useRunningTotal } from './useRunningTotal';
import { BoxTypes, useboxesStore } from '../store/boxesStore';
import { useArrowDeliveryStore } from '../store/arrowDeliveryStore';
import { isUserType } from '../swr/helper';
import { productStoreApi } from '../store/productStore';
import { useEligibilityOffers } from './eligibilityOffers/useEligibilityOffers';
import { getCouponCampaign } from './orderDataBuilders/helper';
import { createUpgradeCustomerDetails } from './orderDataBuilders/cableServices/createUpgradeCustomerDetails';
import {
  assertUpgradeOrderAccountOwnership,
  UpgradeOrderAuthorizationError,
  UPGRADE_ORDER_UNAUTHORIZED_MESSAGE,
} from './upgradeOrderAuthorization';
import { getTreatment } from './useSplitIO';
import { useJSONConfig } from './useJSONConfig';

const getUpgradeJourneyCategory = (hasBroadband: boolean, hasTv: boolean) => {
  if (!hasBroadband) return 'tv';

  return hasTv ? 'tv-broadband' : 'broadband';
};

const emptyJSONConfigService = {
  getConfig: async () => undefined,
} as JSONConfigService;

function useCheckoutReview(
  addressService: AddressService,
  kkService: KonakartService,
  myAccountService: MyAccountService,
  nzPostAddressService: NZPostAddressService,
  orderService: OrderService,
  prequalService: PrequalService,
  trackerService: TrackerService,
  myAccountSWR: MyAccountSWR,
  orderSubmitted: () => void,
  sentryService?: SentryService,
  configService?: JSONConfigService
) {
  const [submitting, setSubmitting] = useState(false);
  const { data: profile } = myAccountSWR.useProfile();
  const { data: billing } = myAccountSWR.useBilling();
  const { data: customer } = myAccountSWR.useCustomer();
  const config = useJSONConfig(configService ?? emptyJSONConfigService);
  const priceIncreaseNotice =
    typeof config?.account?.priceIncreaseNoteComms === 'string'
      ? config.account.priceIncreaseNoteComms
      : undefined;
  const { hasActiveOrPendingMeshBroadbandPackage, isStarterActive } = myAccountSWR.usePackages();
  const { details, deliveryDetails: broadbandDdeliveryDetails, getPrequalData } = useBroadband(prequalService);
  const arrowDeliveryDetails = useArrowDeliveryStore(s => s.details);
  const voiceDetails = useVoiceStore(s => s.details);
  const { addToast, addSuccessToast } = useToastContainer();
  const { filterAndToggleCouponRemovalWithToast } = useCouponRemovalToast(kkService);
  const { upgradableBoxDetails, clearDetails } = useUpgradableBoxesStore(s => ({
    upgradableBoxDetails: s.details,
    clearDetails: s.clearDetails,
  }));

  const { onApplyClicked, couponCodeValue, setCouponCodeValue, hasError } = useCoupon();
  const freeProduct = useFreeProductStore(s => s.freeProduct);
  const {
    coupons,
    monthlyFees,
    oneOffFees,
    allTechnicianFees,
    applyCouponCodeToCart,
    orderTotals,
    cartInitialized,
    order,
    emptyCart,
  } = useCartContainer(kkService, CouponTypeValue.Upgrade);

  const {
    orderProductsAddedToCart,
    orderProductsBoughtInCart,
    existingCampaigns,
    broadbandInCart,
    meshInCart,
    voiceInCart,
    isBroadcastTier,
    currentlySubscribedProducts,
    currentlySubscribedBroadbandPackage,
    dthProductsAddedToCart,
  } = useManagePackage(myAccountSWR);
  const { orderCompleted, vtvMigrationCompleted } = useAnalytics();

  const onCouponApplyClick = async () => {
    await onApplyClicked(applyCouponCodeToCart);
  };

  const {
    setFirstName,
    setWorkOrderNumbers,
    setIsShowConfirmationPage,
    setHasBroadband,
    setHasDevice,
  } = useNewUpgradeOrderStore();
  const { eligibilityOffers, removeEligibilityOffers } = useCouponStore();
  const { newlyAddedMultiroomProducts } = useRunningTotal(kkService, existingCampaigns);

  /* Get the occurrence numbers being upgraded for multiroom as it is the id in the request payload. */

  let orderProductsAdded = [...orderProductsAddedToCart, ...oneOffFees, ...allTechnicianFees];

  const orderProductsDowngraded = order?.orderProducts.filter(p => p.downgrade).map(p => p.product) ?? [];
  const notCurrentlySubscribedOrderProducts = orderProductsAdded.filter(p => !p.currentlySubscribed);

  const { clearBoxStore, boxes: boxesFromStore } = useboxesStore();

  if (freeProduct && freeProduct?.price !== 0) {
    orderProductsAddedToCart.push(freeProduct);
  }

  // we don't have access to the removed products that we want to display so we have to calculate these
  // this is - less than ideal and bit of a hack. Sorry.
  let removedBroadbandProducts: T_Product[] = [];
  if (currentlySubscribedBroadbandPackage && broadbandInCart) {
    removedBroadbandProducts = [currentlySubscribedBroadbandPackage];
    const meshProduct = currentlySubscribedProducts.find(p => p.sku === productSkuIds.meshDevice.primary);
    if (
      meshProduct &&
      (broadbandInCart?.product.sku === productSkuIds.broadbandLightningFastWiFi.primary ||
        broadbandInCart?.product.sku === productSkuIds.broadbandWifi100.primary)
    ) {
      removedBroadbandProducts.push(meshProduct);
    }
  }

  const handleNothingToSubmit = () => {
    addToast({
      title: 'Warning',
      message: 'You do not have anything to upgrade currently. Please add a new package first.',
      type: 'warning',
    });
  };

  const handleSuccess = () => {
    addSuccessToast({
      message: 'Your subscription has been successfully updated',
    });
  };

  const handleBoxUpgradeNotValid = () => {
    addToast({
      title: 'Warning',
      message: 'Installation date/time is not valid for box upgrades.',
      type: 'warning',
    });
  };

  const handleErrors = (error?: Error) => {
    let message =
      error instanceof UpgradeOrderAuthorizationError ? UPGRADE_ORDER_UNAUTHORIZED_MESSAGE : messages.problemWithOrder;
    addToast({
      title: 'Alert',
      message: `${message}`,
      type: 'error',
    });
  };

  const getOrderContext = (details: OrderDetails | undefined, profile: T_Profile | undefined) => {
    return {
      // additional contexts for orders
      contexts: {
        orderDetails: {
          products: JSON.stringify(details?.products),
        },
      },
      user: {
        accountNumber: profile?.accountNumber,
      },
    };
  };

  const getOrderErrorContext = (details: OrderDetails | undefined, profile: T_Profile | undefined, error: any) => {
    const errorCode = error?.code ?? 'UNKNOWN';
    const tuiAddressCode = error?.tuiAddressCode ?? customer?.tuiAddressCode ?? '';

    return {
      ...getOrderContext(details, profile),
      tags: {
        journey: 'upgrade',
        errorCode,
        orderBuilder: error?.orderBuilder ?? 'unknown',
      },
      extra: {
        tuiAddressCode,
        hasInvalidTuiAddressCode: tuiAddressCode === '' || tuiAddressCode === '0',
        bbPlanIndex: error?.bbPlanIndex,
        voiceAdded: error?.voiceAdded,
        hasActiveBroadbandPackage: error?.hasActiveBroadbandPackage,
        installType: error?.installType,
        providerUserId: error?.providerUserId,
        customerAccountNumber: error?.customerAccountNumber ?? customer?.accountNumber ?? '',
      },
    };
  };

  const removeCoupon = async (coupon: T_Coupon) => {
    await filterAndToggleCouponRemovalWithToast(
      monthlyFees,
      cp => !orderProductsBoughtInCart.find(cs => cs.productId === cp.productId),
      coupon
    );
  };

  /**
   * Add BB no contract if there is no other BB coupons
   */
  async function addNoContractDataCoupon(coupons: T_Coupon[], dataVoiceCoupons: T_Coupon[]): Promise<T_Coupon[]> {
    if (!currentlySubscribedBroadbandPackage && broadbandInCart && dataVoiceCoupons.length === 0) {
      const noContractCampaign = await kkService.getCoupon(dataCouponCodeNoContract);
      if (noContractCampaign) return [...coupons, noContractCampaign];
    }
    return coupons;
  }

  /**
   * Existing cart in Upgrade includes calculation for added + existing items to do calculations.
   * Need to only show the added items and promotional pricing for them
   * @param workOrderNumber
   * @param order
   * @param coupon
   */
  const analyticsOrderCompleted = (
    workOrderNumber: string,
    orderProducts: T_OrderProduct[],
    currentOrder: T_Order,
    orderCoupons?: T_Coupon[],
    isVTVCustomer?: boolean
  ) => {
    // we are not doing any multiroom or boxes adding currently -- TODO UPDATE AT FUTURE DATE
    let theOrder = _cloneDeep(currentOrder);
    theOrder.orderProducts = orderProducts;
    let totalIndex = theOrder.orderTotals.findIndex(ot => ot.className === 'ot_total');
    let total = theOrder.orderTotals.find(ot => ot.className === 'ot_total');
    let subtotalIndex = theOrder.orderTotals.findIndex(ot => ot.className === 'ot_subtotal');
    let subtotal = theOrder.orderTotals.find(ot => ot.className === 'ot_subtotal');
    if (total && subtotal) {
      // total - total promotional pricing
      let totalValue = 0;
      theOrder.orderProducts.forEach(op => (totalValue += op.price));
      total.value = totalValue;

      // subtotal - price ignoring promotional stuff
      let subtotalValue = 0;
      theOrder.orderProducts.forEach(op => (subtotalValue += op.product.priceIncTax));
      subtotal.value = subtotalValue;

      theOrder.orderTotals[subtotalIndex] = subtotal;
      theOrder.orderTotals[totalIndex] = total;
    }

    let packageType =
      dthProductsAddedToCart && dthProductsAddedToCart.length > 0 && (broadbandInCart || voiceInCart || meshInCart)
        ? PACKAGE_TYPE.DTH_BROADBAND
        : broadbandInCart || voiceInCart || meshInCart
        ? PACKAGE_TYPE.BROADBAND
        : PACKAGE_TYPE.DTH;

    /* Extra analytics for VTV migration. */
    if (isVTVCustomer) {
      vtvMigrationCompleted();
    } else {
      orderCompleted(workOrderNumber, true, theOrder, orderCoupons, packageType, profile?.accountNumber);
    }
  };

  let acceptBroadbandTermsAndConditionsField = useField<boolean | undefined, string>({
    defaultValue: undefined,
    required: true,
    validations: [
      (value?: boolean) => {
        let errs: string[] = [];
        if (!value) {
          errs.push('Terms must be agreed to');
        }
        return errs;
      },
    ],
  });

  let acceptKeyThingsToKnowField = useField<boolean | undefined, string>({
    defaultValue: undefined,
    required: true,
    validations: [
      (value?: boolean) => {
        let errs: string[] = [];
        if (!value) {
          errs.push('Terms must be agreed to');
        }
        return errs;
      },
    ],
  });

  const termsAndConditionsField = useTermsAndConditions();
  const keyThingsToKnowField = useKeyThingsToKnowField();
  const boxes = useboxesStore(s => s.boxes);
  const hasNonPod = boxes.some(
    b => b.boxType === 'NEW' && !b.products.some(p => p.sku === productSkuIds.skyPod.primary)
  );
  const hasNonPodUpgrade = boxes.some(
    b => b.boxType === BoxTypes.UPGRADE && !b.products.some(p => p.sku === productSkuIds.skyPod.primary)
  );
  const hasNonPodBox = hasNonPod || hasNonPodUpgrade;
  const { vtvOffer } = useEligibilityOffers();

  const form = useForm({
    fields: [termsAndConditionsField, keyThingsToKnowField],
    onSubmit: async () => {
      setSubmitting(true);
      // for any random chance the box upgrade is not valid should not do an order
      if (!upgradableBoxDetails.isValid && hasNonPod) {
        handleBoxUpgradeNotValid();
        setSubmitting(false);
        return;
      }

      let order = orderStoreApi.getState().order!;

      // (person has existing campaign OR has no new coupon added) AND has nothing in the cart including multiroom so no reason to submit.
      if (
        orderProductsAdded.length === 0 &&
        orderProductsDowngraded.length === 0 &&
        newlyAddedMultiroomProducts?.length === 0 &&
        boxesFromStore.filter(box => box.boxType !== 'SUBSCRIBED').length === 0 &&
        eligibilityOffers?.eligibilityIncludeCampaign?.length === 0
      ) {
        handleNothingToSubmit();
        setSubmitting(false);
      } else {
        const customerInfo = buildCustomerInfo(customer!);
        let orderDetail: OrderDetails | undefined;
        const dataVoiceCoupons = coupons.filter(
          c => c.custom5 === CouponTypes.Broadband || c.custom5 === CouponTypes.Voice
        );
        const couponsToSend = await addNoContractDataCoupon(coupons, dataVoiceCoupons);

        const isVTVCustomer = isUserType(profile?.groups ?? [], UserGroupType.SKY_VTV_CUSTOMER);
        if (billing?.services.CableServiceOccurrence) {
          /**
           * Filtering occurrences type HHOCC and IPTV in billing for a VTV customer
           */
          const cableOccurrences = isVTVCustomer
            ? billing?.services?.CableServiceOccurrence.filter(occ => !VTVOccurrenceTypes.includes(occ.occurrenceType))
            : billing?.services.CableServiceOccurrence;
          billing.services.CableServiceOccurrence = cableOccurrences;
        }

        let requestOrderProductsAdded: T_OrderProduct[] = orderProductsAdded;
        if (isVTVCustomer) {
          /**
           * For VTV orders, we just need the starter package added to the box.
           */
          requestOrderProductsAdded = [...orderProductsBoughtInCart, ...orderProductsAdded].filter(
            p => p.product.sku === productSkuIds.starter.primary
          );
        }
        const customerDetails = await createUpgradeCustomerDetails(
          customer!,
          upgradableBoxDetails,
          hasNonPodBox,
          addressService
        );
        try {
          orderDetail = await createRequestBody(
            trackerService,
            addressService,
            nzPostAddressService,
            details,
            broadbandDdeliveryDetails,
            requestOrderProductsAdded.map(op => op.product),
            orderProductsDowngraded,
            customerInfo,
            billing?.services,
            couponsToSend,
            getPrequalData,
            upgradableBoxDetails,
            voiceDetails,
            currentlySubscribedBroadbandPackage,
            hasActiveOrPendingMeshBroadbandPackage,
            isStarterActive,
            arrowDeliveryDetails,
            customer?.fusionRegion,
            isVTVCustomer,
            VTVMigrationType.TRANSFER,
            billing?.campaigns
          );

          const eligibilityExcludeCoupons = getCouponCampaign(eligibilityOffers?.eligibilityExcludeCoupons) || [];
          eligibilityExcludeCoupons?.forEach(c => (c.action = 'remove'));
          const freeProductId = order.orderTotals.find(p => p.className === 'ot_free_product')?.freeProductId;
          const freeProduct = order.orderProducts.find(p => p.productId === freeProductId);
          // Add the free product to order products added for upgrade order emails
          if (freeProduct && freeProduct.price !== 0) {
            orderProductsAdded.push(freeProduct);
          }

          if (orderDetail && profile?.accountNumber && customer?.houseNumber) {
            if (isVTVCustomer) {
              /**
               * For VTV customers, we need to add the orders bought in cart so we can show them
               * their Sky products acquired on the email receipt.
               */
              orderProductsAdded = [...orderProductsBoughtInCart, ...orderProductsAdded];

              /**
               * Adds mandatory campaign code for Dart rate EE campaign
               * Dart rates
               * 10% - 4903 - Pass 4904 additionally if Soho or Hindi+1 is in order
               * 20% - 4914 - Pass 4915 additionally if Soho or Hindi+1 is in order
               */
              orderDetail?.products.forEach(product => {
                if (
                  product.type === 'cable' &&
                  product.campaigns?.some(c =>
                    [...DartEligibilityCode.Discount10, ...DartEligibilityCode.Discount20].includes(c.id)
                  )
                ) {
                  const hindiSvc = productStoreApi
                    .getState()
                    .products.filter(p => p.categoryId === categoryIds.hindiChannels)
                    .map(p => p.sku);
                  const addedSvc = orderProductsAdded.map(p => p.product.sku);
                  if (
                    hindiSvc.filter(a => addedSvc.includes(a))?.length > 1 ||
                    addedSvc.includes(productSkuIds.soho.primary)
                  ) {
                    if (product.campaigns?.some(c => c.id === DartEligibilityCode.Discount10[0])) {
                      product.campaigns?.push({
                        id: DartEligibilityCode.Discount10[1],
                        action: 'add',
                        description: '',
                      });
                    } else if (product.campaigns?.some(c => c.id === DartEligibilityCode.Discount20[0])) {
                      product.campaigns?.push({
                        id: DartEligibilityCode.Discount20[1],
                        action: 'add',
                        description: '',
                      });
                    }
                  }
                }
              });
          }
          //include campaigns which needs to be removed
          orderDetail.products.forEach(p => {
            p.campaigns?.forEach(c => {
              c.action = 'add';
            });
            p.campaigns?.push(...eligibilityExcludeCoupons);
          });
          assertUpgradeOrderAccountOwnership(profile.accountNumber, customer.accountNumber);
          const hasBroadbandUpgrade = Boolean(broadbandInCart || meshInCart || voiceInCart);
          const hasTvUpgrade = Boolean(dthProductsAddedToCart?.length);
          const journeyCategory = getUpgradeJourneyCategory(hasBroadbandUpgrade, hasTvUpgrade);
          const { enabled: showPriceIncreaseNotice } = await getTreatment(
            SPLITIO_KEY.SKY_WEB_PRICE_INCREASE_COMMS,
            { category: journeyCategory }
          );
          const response = await orderService.postUpgradeOrderProcessor(
            order.orderTotals,
            isVTVCustomer ? orderProductsAdded : notCurrentlySubscribedOrderProducts,
              customer!,
              orderDetail,
              existingCampaigns,
              customerDetails,
              isVTVCustomer,
              vtvOffer?.eligibilityID ?? undefined,
              hasBroadbandUpgrade && showPriceIncreaseNotice
                ? priceIncreaseNotice
                : undefined
            );
            removeEligibilityOffers();
            await emptyCart();

            setSubmitting(false);
            handleSuccess();

            let orderNumbers: OrderNumber[] = [];
            const workOrderNumber = response?.initialOrderId;
            // As per discussion with Cam & Kristina, one order/upgrade should suffice.
            order && analyticsOrderCompleted(workOrderNumber, orderProductsAdded, order, coupons, isVTVCustomer);

            let orderNumber: OrderNumber = { value: workOrderNumber, techVisit: false };
            orderNumbers = [orderNumber];

            // analytics end

            // log to sentry
            if (response?.errors?.length > 0) {
              const error = new Error(
                `Upgrade order error ${response.errors[0].description}: ${response.errors[0].message}`
              );
              sentryService?.captureException(error, getOrderContext(orderDetail, profile));
            }
            // Store order response data so we can show it in the confirmation page.
            setWorkOrderNumbers(orderNumbers);
            setFirstName(profile?.firstName);
            setIsShowConfirmationPage(true);
            setHasBroadband(broadbandInCart !== undefined || meshInCart !== undefined || voiceInCart !== undefined);
            setHasDevice(boxes?.some(b => b.boxType === 'NEW' || b.boxType === 'UPGRADE'));
            orderSubmitted();
            clearBoxStore();
            clearDetails();
            setOrderRequest([]);
          }
        } catch (err) {
          setSubmitting(false);
          handleErrors(err);
          // HTTP errors are now reported in axios response interceptor
          // We send errors here as Order errors with extra contexts for debugging and easier filtering
          sentryService?.captureException(err, getOrderErrorContext(orderDetail, profile, err));
        }
      }
    },
  });

  // Get the current monthly charge based on current subscribe products
  const currentSubscription = useMemo(() => {
    // things in the cart that are part of the current subscription
    return monthlyFees.filter(mf => currentlySubscribedProducts.find(csp => csp.id === mf.productId) != null);
  }, [monthlyFees, currentlySubscribedProducts]);

  // take into account the number of multirooms a user has.
  const currentMonthlyCost = useMemo(() => formatMoney(_sumBy(currentSubscription, p => p.price * p.quantity)), [
    currentSubscription,
  ]);

  return {
    form,
    orderProductsBoughtInCart,
    orderProductsAddedToCart,
    removedBroadbandProducts,
    coupons,
    applyCouponCodeToCart,
    removeCoupon,
    monthlyFees,
    oneOffFees,
    orderTotals,
    existingCampaigns,
    submitting,
    broadbandInCart,
    currentlySubscribedBroadbandPackage,
    acceptBroadbandTermsAndConditionsField,
    acceptKeyThingsToKnowField,
    keyThingsToKnowField,
    termsAndConditionsField,
    voiceInCart,
    meshInCart,
    cartInitialized,
    currentMonthlyCost,
    isBroadcastTier,
    couponCodeValue,
    setCouponCodeValue,
    hasError,
    freeProduct,
    onCouponApplyClick,
    currentSubscription,
  };
}

export { useCheckoutReview };
